import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, MessageCircle, TrendingUp, ArrowRight } from 'lucide-react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Link } from 'react-router-dom';

interface Spor {
  id: string;
  name: string;
  icon: string;
  matchCount: number;
  commentCount: number;
  trend: 'up' | 'down' | 'stable';
}

const KonusulanSporlar: React.FC = () => {
  const [sporlar, setSporlar] = useState<Spor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sporlarQuery = query(
      collection(db, 'konusulanSporlar'),
      orderBy('commentCount', 'desc'),
      limit(6)
    );

    const unsubscribe = onSnapshot(sporlarQuery, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Spor[];
      setSporlar(data);
      setLoading(false);
    }, () => {
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Demo veriler
  const demoSporlar = [
    { id: '1', name: 'Futbol', icon: '⚽', matchCount: 245, commentCount: 12500, trend: 'up' as const },
    { id: '2', name: 'Basketbol', icon: '🏀', matchCount: 128, commentCount: 8700, trend: 'up' as const },
    { id: '3', name: 'Tenis', icon: '🎾', matchCount: 89, commentCount: 5400, trend: 'stable' as const },
    { id: '4', name: 'Voleybol', icon: '🏐', matchCount: 67, commentCount: 3200, trend: 'up' as const },
    { id: '5', name: 'E-Spor', icon: '🎮', matchCount: 156, commentCount: 9800, trend: 'up' as const },
    { id: '6', name: 'Buz Hokeyi', icon: '🏒', matchCount: 45, commentCount: 2100, trend: 'down' as const },
  ];

  const displayData = sporlar.length > 0 ? sporlar : demoSporlar;

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">En Çok Konuşulan Sporlar</h2>
        </div>
        <Link to="/spor-bahisleri" className="text-primary-400 hover:text-primary-300 text-sm">
          Tümünü Gör →
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 flex-1">
        {loading ? (
          <div className="col-span-3 animate-pulse">
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-24 bg-dark-700/50 rounded-lg" />
              ))}
            </div>
          </div>
        ) : (
          displayData.map((spor, index) => (
            <motion.div
              key={spor.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group p-4 bg-dark-700/30 rounded-xl hover:bg-dark-700/50 transition-all cursor-pointer border border-transparent hover:border-primary-500/30"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-3xl">{spor.icon}</span>
                <TrendingUp className={`w-4 h-4 ${
                  spor.trend === 'up' ? 'text-green-400' :
                  spor.trend === 'down' ? 'text-red-400' : 'text-gray-400'
                }`} />
              </div>
              <h3 className="text-white font-semibold mb-1">{spor.name}</h3>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">{spor.matchCount} maç</span>
                <div className="flex items-center space-x-1 text-gray-400">
                  <MessageCircle className="w-3 h-3" />
                  <span>{(spor.commentCount / 1000).toFixed(1)}k</span>
                </div>
              </div>
              <div className="mt-3 flex items-center text-primary-400 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                <span>Bahis Yap</span>
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default KonusulanSporlar;
